import { useEffect, useRef, useCallback } from 'react';
import { getAuth, signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';

interface UseIdleTimeoutProps {
  timeout?: number; // in milliseconds, default 15 minutes
  onIdle?: () => void;
}

export const useIdleTimeout = ({
  timeout = 15 * 60 * 1000, // 15 minutes default
  onIdle
}: UseIdleTimeoutProps = {}) => {
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const auth = getAuth();

  const handleLogout = useCallback(async () => {
    try {
      await signOut(auth);
      toast.error('You have been logged out due to inactivity');
      navigate('/login');
    } catch (error) {
    }
  }, [auth, navigate]);

  const handleIdle = useCallback(() => {
    if (onIdle) {
      onIdle();
    } else {
      handleLogout();
    }
  }, [onIdle, handleLogout]);

  const resetTimer = useCallback(() => {
    // Clear existing timeout
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }

    // Set new timeout
    timeoutIdRef.current = setTimeout(() => {
      handleIdle();
    }, timeout);
  }, [timeout, handleIdle]);

  useEffect(() => {
    // Events that indicate user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    // Reset timer on any user activity
    const handleActivity = () => {
      resetTimer();
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, handleActivity);
    });

    // Start the initial timer
    resetTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, [resetTimer]);

  return { resetTimer };
};